import { useCallback } from 'react'
import {
  ApolloQueryResult,
  LazyQueryHookOptions,
  useApolloClient,
  useLazyQuery,
} from '@apollo/client'
import getCheckoutRuleQuery from './query'
import {
  IGetCheckoutRuleResponse,
  IGetCheckoutRuleVariables,
} from './types'

/**
 * Lazy, so a screen fetches on mount rather than on every render.
 *
 * **`no-cache` is a pin, not a default, and the key order below is
 * load-bearing.** Caching here would be wrong rather than merely stale:
 * `DELIVERY_DATES` is computed from the clock at request time and filtered to
 * days strictly after today, so a cached answer goes wrong at midnight in the
 * storefront's timezone with nothing to invalidate it. The variables are the
 * storefront, the rule type and the session — all constant for a session — so
 * Apollo would key every fetch identically and serve the first answer for the
 * rest of it.
 *
 * The policy options are omitted from the parameter so the pin cannot be
 * defeated from a call site: leaving them in meant `fetchPolicy` still
 * compiled and was merely ignored, and `nextFetchPolicy` /
 * `initialFetchPolicy` would still have won outright. Apollo lets the
 * *execute* call override the policy as well, which a wrapper cannot prevent
 * without changing what it returns — nothing does that here. Same shape as
 * `linkedProducts/hook.ts`, for the same class of reason.
 *
 * @param options: LazyQueryHookOptions without the fetch-policy keys
 * @internal
 */
const useLazyGetCheckoutRule = (
  options?: Omit<
    LazyQueryHookOptions<IGetCheckoutRuleResponse, IGetCheckoutRuleVariables>,
    'fetchPolicy' | 'nextFetchPolicy' | 'initialFetchPolicy'
  >,
) => {
  return useLazyQuery<IGetCheckoutRuleResponse, IGetCheckoutRuleVariables>(
    getCheckoutRuleQuery,
    {
      ...options,
      fetchPolicy: 'no-cache',
    },
  )
}

/**
 * A function that runs `getCheckoutRule` once per call, for a caller that
 * asks about both rule types at once (COM-1501's `startGuidedWalk`).
 *
 * Built on `client.query` rather than `useLazyQuery`, for the reason
 * `usePromotionItemsAvailabilityQuery` gives: one lazy execute re-drives one
 * observable, so two concurrent calls with different `ruleType`s are not two
 * independent requests. It also answers a failure as a rejection rather than
 * the lazy hook's Apollo 3.12 habit of resolving a failed read with the
 * previous successful data.
 *
 * Variables only, so the pins cannot be overridden from a call site:
 * `no-cache` for the reason on `useLazyGetCheckoutRule` above (a cached
 * `DELIVERY_DATES` answer goes wrong at midnight), and `errorPolicy: 'none'`
 * so a 404 for an unserved rule type rejects instead of resolving empty.
 * @internal
 */
const useGetCheckoutRuleQuery = () => {
  const client = useApolloClient()
  return useCallback(
    (
      variables: IGetCheckoutRuleVariables,
    ): Promise<ApolloQueryResult<IGetCheckoutRuleResponse>> =>
      client.query<IGetCheckoutRuleResponse, IGetCheckoutRuleVariables>({
        query: getCheckoutRuleQuery,
        variables,
        fetchPolicy: 'no-cache',
        errorPolicy: 'none',
      }),
    [client],
  )
}

export { useLazyGetCheckoutRule, useGetCheckoutRuleQuery }
